/**
 * Every programme in the tenant, and the delivery status and end of life a
 * Client Admin can give it.
 *
 * Delivery is separate from design approval: a programme can be approved and
 * not yet running, so the two states sit in different columns.
 *
 * @module components/admin/ProgrammeDeliveryTable
 */

import React from "react";

import { deliveryLabel, type DeliveryStatusState } from "@/components/admin/useDeliveryStatus";
import { programmeFilterDefs } from "@/components/shared/programme-filter-defs";
import { isRetired } from "@/components/shared/programme-retirement";
import { recordVersionLabel } from "@/components/shared/revision-labels";
import { WorkflowStateBadge } from "@/components/shared/WorkflowStateBadge";
import {
  Alert,
  Badge,
  Button,
  CollectionView,
  type Column,
  EmptyState,
  Heading,
  MutedText,
  type RowActionsSpec,
  SelectField,
  TextField,
  useCollectionFilters,
} from "@/components/ui";
import type { ProgrammeSummary } from "@/platform";
import { isDeliveryStatusApplicable } from "@/server/programme-delivery-policy";
import type { ProgrammeDeliveryStatus } from "@/server/types";
import { formatCalendarDate } from "@/utils/calendar-dates";
import { getRegion } from "@/utils/format-instant";

const DELIVERY_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const END_OF_LIFE_HELP_ID = "programme-end-of-life-help";

/**
 * Why this programme's delivery cannot be changed right now, or "" when it
 * can. One function rather than a copy per column, so the select and the
 * Apply button beside it can never disagree about whether they are refused.
 *
 * @param programme - The row's programme.
 * @param busyProgrammeId - The programme a delivery write is in flight for.
 * @returns The reason, ready to render and to be pointed at by a description.
 */
function refusalFor(programme: ProgrammeSummary, busyProgrammeId: string | null): string {
  if (!isDeliveryStatusApplicable(programme.workflowState)) {
    return "Available after approval";
  }
  if (isRetired(programme)) {
    return "Retired programmes cannot change delivery";
  }
  if (busyProgrammeId !== null) {
    return "Waiting for the delivery change already in flight";
  }
  return "";
}

export interface ProgrammeDeliveryTableProps {
  programmes: readonly ProgrammeSummary[];
  delivery: DeliveryStatusState;
  onCreateProgramme?: () => void;
  onViewProgramme?: (programmeId: string) => void;
  onManageBuilders?: (programmeId: string) => void;
  managingProgrammeId?: string | null;
}

/**
 * @example
 * ```tsx
 * <ProgrammeDeliveryTable programmes={programmes} delivery={delivery} … />
 * ```
 */
export function ProgrammeDeliveryTable({
  programmes,
  delivery,
  onCreateProgramme = undefined,
  onViewProgramme = undefined,
  onManageBuilders = undefined,
  managingProgrammeId = undefined,
}: ProgrammeDeliveryTableProps): React.ReactElement {
  const { rows, filtering } = useCollectionFilters(
    programmes,
    programmeFilterDefs({
      workflow: "admin-programmes-workflow",
      level: "admin-programmes-level",
      discipline: "admin-programmes-discipline",
      text: "admin-programmes-search",
    }),
    { label: "Filter programmes", noun: "programme" },
  );

  const columns: readonly Column<ProgrammeSummary>[] = [
    {
      key: "programme",
      header: "Programme",
      render: (programme) => (
        <div>
          <div className="font-semibold">{programme.title}</div>
          <MutedText as="span" size="caption">
            {recordVersionLabel(programme.saveRevision)}
          </MutedText>
        </div>
      ),
      sortValue: (programme) => programme.title,
    },
    {
      key: "workflow",
      header: "Approval",
      render: (programme) => <WorkflowStateBadge state={programme.workflowState} />,
      priority: 2,
    },
    {
      key: "delivery",
      header: "Delivery",
      render: (programme) => (
        <Badge
          tone={programme.deliveryStatus && !isRetired(programme) ? "ink" : "neutral"}
          variant="soft"
          size="sm"
        >
          {deliveryLabel(
            programme.deliveryStatus,
            isDeliveryStatusApplicable(programme.workflowState),
            isRetired(programme),
          )}
        </Badge>
      ),
    },
    {
      key: "change",
      header: "Change to",
      render: (programme) => {
        // Before approval there is nothing to choose, so one message says so
        // in place of a select that could only refuse.
        // The Delivery column already says why, so this one stays empty.
        if (!isDeliveryStatusApplicable(programme.workflowState)) {
          return <span aria-hidden>—</span>;
        }
        const refusal = refusalFor(programme, delivery.busyProgrammeId);
        const noteId = `programme-delivery-note-${programme.id}`;
        return (
          <div className="min-w-40">
            <SelectField
              id={`programme-delivery-status-${programme.id}`}
              value={delivery.drafts[programme.id] ?? programme.deliveryStatus ?? "inactive"}
              onChange={(value) => {
                delivery.setDraft(programme.id, value as ProgrammeDeliveryStatus);
              }}
              options={DELIVERY_OPTIONS}
              disabled={Boolean(refusal)}
              size="sm"
              aria-label={`Delivery status for ${programme.title}`}
              aria-describedby={refusal ? noteId : undefined}
              data-testid={`programme-delivery-select-${programme.id}`}
            />
            {refusal ? (
              <span id={noteId}>
                <MutedText as="span" size="caption">
                  {refusal}
                </MutedText>
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "end-of-life",
      header: "End of life",
      render: (programme) => {
        if (!isDeliveryStatusApplicable(programme.workflowState)) {
          return <span aria-hidden>—</span>;
        }
        if (isRetired(programme) && programme.endOfLife) {
          return (
            <span data-testid={`programme-end-of-life-${programme.id}`}>
              Retired {formatCalendarDate(programme.endOfLife, getRegion().locale)}
            </span>
          );
        }
        return (
          <div className="min-w-40">
            <TextField
              id={`programme-end-of-life-${programme.id}`}
              type="date"
              value={delivery.endOfLifeDrafts[programme.id] ?? ""}
              onChange={(value) => {
                delivery.setEndOfLifeDraft(programme.id, value);
              }}
              disabled={delivery.busyProgrammeId !== null}
              size="sm"
              aria-label={`End of life for ${programme.title}`}
              aria-describedby={END_OF_LIFE_HELP_ID}
              data-testid={`programme-end-of-life-${programme.id}`}
            />
          </div>
        );
      },
      sortValue: (programme) => programme.endOfLife ?? "",
    },
  ];

  // Apply is the one inline action, and only on a row whose choice differs
  // from what is stored; every other action waits in the menu.
  const rowActions: RowActionsSpec<ProgrammeSummary> = {
    label: (programme) => programme.title,
    items: (programme) => {
      const draft = delivery.drafts[programme.id];
      const endOfLifeDraft = delivery.endOfLifeDrafts[programme.id];
      const changed =
        (draft !== undefined && draft !== (programme.deliveryStatus ?? "inactive")) ||
        (endOfLifeDraft !== undefined && endOfLifeDraft !== (programme.endOfLife ?? ""));
      const self = delivery.busyProgrammeId === programme.id;
      // Another row's write refuses this one, but a row's own write is what
      // its Apply is showing, so it must not be refused out of view.
      const refused = Boolean(refusalFor(programme, self ? null : delivery.busyProgrammeId));
      return [
        changed &&
          !refused && {
            key: "apply",
            label: "Apply",
            icon: "check",
            primary: true,
            loading: self,
            onSelect: () => {
              delivery.apply(programme);
            },
            "data-testid": `programme-delivery-apply-${programme.id}`,
          },
        onViewProgramme && {
          key: "view",
          label: "View",
          icon: "eye",
          onSelect: () => {
            onViewProgramme(programme.id);
          },
          "data-testid": `admin-view-programme-${programme.id}`,
        },
        onManageBuilders && {
          key: "team",
          label: managingProgrammeId === programme.id ? "Close team" : "Team",
          icon: "users-three",
          onSelect: () => {
            onManageBuilders(programme.id);
          },
          "data-testid": `admin-manage-builders-${programme.id}`,
        },
      ];
    },
  };

  return (
    <div className="space-y-4" data-testid="programme-delivery-management">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Heading level={2} size="md">
          Tenant programmes
        </Heading>
        {onCreateProgramme ? (
          <Button
            size="sm"
            variant="primary"
            icon="plus-circle"
            onClick={onCreateProgramme}
            data-testid="admin-create-programme"
          >
            Create programme
          </Button>
        ) : null}
      </div>
      <div id={END_OF_LIFE_HELP_ID}>
        <MutedText size="caption">
          A programme retires on its end of life. From that day its publication closes and it can no
          longer change; its approved revisions stay readable.
        </MutedText>
      </div>

      {/* Mounted whatever the outcome: a live region inserted together with
          its text is commonly missed, so a refused write would change the
          screen in silence. */}
      <div role="status" aria-live="polite" data-testid="programme-delivery-feedback">
        {delivery.feedback ? (
          <Alert tone={delivery.feedback.tone === "success" ? "success" : "warning"}>
            {delivery.feedback.message}
          </Alert>
        ) : null}
      </div>

      <CollectionView
        storageKey="admin-programmes"
        caption="Tenant programmes"
        columns={columns}
        rows={rows}
        rowKey={(programme) => programme.id}
        filtering={filtering}
        verticalAlign="top"
        rowActions={rowActions}
        rowTestId={(programme) => `admin-programme-row-${programme.id}`}
        emptyState={
          <EmptyState icon="funnel" title="No programmes match the current filters" density="row" />
        }
        data-testid="admin-programme-table"
      />
    </div>
  );
}
